import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, setStoredToken } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email: (email || "").trim(), password });
            if (data?.access_token) setStoredToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    }, []);

    const register = useCallback(async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data?.access_token) setStoredToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setStoredToken(null);
        setUser(false);
    }, []);

    // Memoise the provider value so downstream consumers don't re-render on
    // every parent re-render — the object identity only changes when user
    // or loading actually change.
    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh }),
        [user, loading, login, register, logout, refresh],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
